/**
 * 小票 ESC/POS 模板生成器
 * 支持 58mm（32列）和 80mm（48列）热敏打印机，中文采用 GBK 编码
 */

import iconv from 'iconv-lite'

/** 80mm 热敏纸常用列宽（如佳博 GP-L80180I） */
export const RECEIPT_COLUMNS_80MM = 48

const ESC = 0x1b
const GS = 0x1d
const FS = 0x1c

export interface ReceiptOrderDetail {
  name?: string
  number?: number
  amount?: number
  dishFlavor?: string
}

export interface ReceiptOrder {
  number?: string | number
  orderTime?: string
  /** 1 外送 2 自提 3 堂食 */
  orderType?: number | string
  consignee?: string
  phone?: string
  address?: string
  remark?: string
  estimatedDeliveryTime?: string
  deliveryFee?: number
  amount?: number
  payMethod?: number
  checkoutTime?: string
  orderDetailList?: ReceiptOrderDetail[]
}

export interface ReceiptOptions {
  /** 纸张宽度对应的字符列数，58mm 推荐 32，80mm 推荐 48 */
  columns?: number
  /** 店铺名称，打印在顶部 */
  shopName?: string
}

class EscPosBuilder {
  private chunks: number[][] = []
  private columns: number

  constructor(columns: number) {
    this.columns = columns
  }

  private push(...bytes: number[]) {
    this.chunks.push(bytes)
    return this
  }

  private pushBytes(bytes: Uint8Array | number[]) {
    this.chunks.push(Array.from(bytes))
    return this
  }

  init() {
    return this.push(ESC, 0x40)
  }

  /** 切换中文字符模式（FS &）；退出：FS .  */
  enableChinese() {
    return this.push(FS, 0x26)
  }

  /** 选择字符代码页为 GBK（CP936，选 Page 15 在多数中国热敏打印机上对应 GBK） */
  codepageGBK() {
    return this.push(ESC, 0x74, 0x0f)
  }

  /** 0=左 1=中 2=右 */
  align(value: 'left' | 'center' | 'right') {
    const map = { left: 0, center: 1, right: 2 }
    return this.push(ESC, 0x61, map[value])
  }

  bold(on: boolean) {
    return this.push(ESC, 0x45, on ? 1 : 0)
  }

  /**
   * 字号放大：width/height 为 1~8 倍
   */
  size(width: number, height: number) {
    const w = Math.max(1, Math.min(8, width)) - 1
    const h = Math.max(1, Math.min(8, height)) - 1
    return this.push(GS, 0x21, (w << 4) | h)
  }

  /** 下划线 0=无 1=单线 2=双线 */
  underline(value: 0 | 1 | 2) {
    return this.push(ESC, 0x2d, value)
  }

  newline(count = 1) {
    for (let i = 0; i < count; i++) this.push(0x0a)
    return this
  }

  /** 走纸 n 行 */
  feed(n: number) {
    return this.push(ESC, 0x64, Math.max(0, Math.min(255, n)))
  }

  /** 全切（部分打印机只支持半切） */
  cut() {
    return this.push(GS, 0x56, 0x01)
  }

  text(value: string) {
    if (!value) return this
    const buf = iconv.encode(value, 'gbk')
    return this.pushBytes(buf)
  }

  line(value: string) {
    return this.text(value).newline()
  }

  /** 水平分割线 */
  rule(char = '-') {
    return this.line(char.repeat(this.columns))
  }

  /**
   * 两端对齐的一行：左文字 右文字（按列宽补空格）
   */
  twoCol(left: string, right: string) {
    const leftWidth = this.strWidth(left)
    const rightWidth = this.strWidth(right)
    const space = Math.max(1, this.columns - leftWidth - rightWidth)
    return this.line(left + ' '.repeat(space) + right)
  }

  /** 三列布局：左 中 右；用于 菜品名称 / 数量 / 金额 */
  threeCol(
    left: string,
    middle: string,
    right: string,
    widths: [number, number, number] = [
      Math.floor(this.columns * 0.58),
      Math.floor(this.columns * 0.17),
      this.columns - Math.floor(this.columns * 0.58) - Math.floor(this.columns * 0.17),
    ],
  ) {
    const leftCell = this.padEnd(left, widths[0])
    const midCell = this.padStart(middle, widths[1])
    const rightCell = this.padStart(right, widths[2])
    return this.line(leftCell + midCell + rightCell)
  }

  /** 计算字符宽度：ASCII 1 列，CJK 2 列 */
  private strWidth(s: string): number {
    let w = 0
    for (const ch of s) {
      const code = ch.codePointAt(0) || 0
      w += code > 0x7f ? 2 : 1
    }
    return w
  }

  /** 按显示宽度右侧补空格截断 */
  private padEnd(s: string, width: number): string {
    const truncated = this.truncate(s, width)
    const pad = width - this.strWidth(truncated)
    return truncated + ' '.repeat(Math.max(0, pad))
  }

  private padStart(s: string, width: number): string {
    const truncated = this.truncate(s, width)
    const pad = width - this.strWidth(truncated)
    return ' '.repeat(Math.max(0, pad)) + truncated
  }

  private truncate(s: string, width: number): string {
    let out = ''
    let w = 0
    for (const ch of s) {
      const code = ch.codePointAt(0) || 0
      const cw = code > 0x7f ? 2 : 1
      if (w + cw > width) break
      out += ch
      w += cw
    }
    return out
  }

  build(): Uint8Array {
    const total = this.chunks.reduce((s, c) => s + c.length, 0)
    const out = new Uint8Array(total)
    let offset = 0
    for (const c of this.chunks) {
      out.set(c, offset)
      offset += c.length
    }
    return out
  }
}

function formatMoney(v: number | undefined | null): string {
  if (v == null || isNaN(Number(v))) return '0.00'
  return Number(v).toFixed(2)
}

/** 明细：amount 为单价，number 为数量，行金额 = 单价 × 数量（分） */
function orderDetailLineCents(item: ReceiptOrderDetail): number {
  const unit = Number(item && item.amount) || 0
  const qty = Number(item && item.number) || 1
  return Math.round(unit * qty * 100)
}

/** 前端计算菜品小计（避免浮点精度） */
function calcDishTotal(list: ReceiptOrderDetail[] | undefined): string {
  if (!list || !list.length) return '0.00'
  const cents = list.reduce((sum, item) => sum + orderDetailLineCents(item), 0)
  return (cents / 100).toFixed(2)
}

function getPayMethodText(method?: number): string {
  if (method === 1) return '微信支付'
  if (method === 2) return '支付宝支付'
  return '其他'
}

/** orderType：1 外送 2 自提 3 堂食 */
function getOrderTypeText(orderType?: number | string | null): string {
  const n = Number(orderType)
  if (n === 1) return '外送'
  if (n === 2) return '自提'
  if (n === 3) return '堂食'
  if (orderType === '' || orderType === undefined || orderType === null) {
    return '—'
  }
  return String(orderType)
}

function charDisplayWidth(ch: string): number {
  const code = ch.codePointAt(0) || 0
  return code > 0x7f ? 2 : 1
}

/** 按热敏显示宽度折行（ASCII 1、中文等 2），不截断 */
function wrapToWidth(s: string, maxWidth: number): string[] {
  if (maxWidth < 1) return [s || '']
  if (!s) return ['']
  const lines: string[] = []
  let buf = ''
  let w = 0
  for (const ch of s) {
    const cw = charDisplayWidth(ch)
    if (cw > maxWidth) {
      if (buf) {
        lines.push(buf)
        buf = ''
        w = 0
      }
      lines.push(ch)
      continue
    }
    if (w + cw > maxWidth) {
      lines.push(buf)
      buf = ch
      w = cw
    } else {
      buf += ch
      w += cw
    }
  }
  if (buf.length) lines.push(buf)
  return lines.length ? lines : ['']
}

function emitWrappedLines(b: EscPosBuilder, text: string, columns: number) {
  for (const line of wrapToWidth(text, columns)) {
    b.line(line)
  }
}

function dishColWidths(columns: number): [number, number, number] {
  const w1 = Math.floor(columns * 0.58)
  const w2 = Math.floor(columns * 0.17)
  const w3 = columns - w1 - w2
  return [w1, w2, w3]
}

/**
 * 生成订单小票的 ESC/POS 字节流
 */
export function buildOrderReceipt(order: ReceiptOrder, options: ReceiptOptions = {}): Uint8Array {
  const columns = options.columns || 32
  const shopName = options.shopName || '重庆荣昌川菜馆'
  const b = new EscPosBuilder(columns)

  b.init().enableChinese().codepageGBK()

  b.align('center').size(2, 2).bold(true).line(shopName).bold(false).size(1, 1)
  b.align('center').line('订单小票')
  b.newline()

  /** 订单信息 + 客户信息：加高字号，长文本自动折行 */
  b.size(1, 2)
  b.align('left')
  emitWrappedLines(b, `订单号: ${order.number != null ? String(order.number) : ''}`, columns)
  emitWrappedLines(b, `下单时间: ${order.orderTime || ''}`, columns)
  emitWrappedLines(b, `取餐方式: ${getOrderTypeText(order.orderType)}`, columns)
  b.rule()

  emitWrappedLines(b, `姓名: ${order.consignee || ''}`, columns)
  emitWrappedLines(b, `电话: ${order.phone || ''}`, columns)
  emitWrappedLines(b, `地址: ${order.address || ''}`, columns)
  if (order.remark) {
    b.bold(true)
    emitWrappedLines(b, `备注: ${order.remark}`, columns)
    b.bold(false)
  }
  b.rule()

  /** 菜品行与金额汇总：加高字号；菜名过长按品名列宽折行，不截断 */
  b.threeCol('品名', '数量', '金额')
  b.rule()

  const details = order.orderDetailList || []
  const widths = dishColWidths(columns)
  for (const item of details) {
    const lineYuan = orderDetailLineCents(item) / 100
    const nameLines = wrapToWidth(item.name || '', widths[0])
    const numStr = `x${item.number || 1}`
    const moneyStr = formatMoney(lineYuan)
    b.threeCol(nameLines[0] || '', numStr, moneyStr, widths)
    for (let i = 1; i < nameLines.length; i++) {
      b.threeCol(nameLines[i], '', '', widths)
    }
    const flavor = item.dishFlavor && String(item.dishFlavor).trim()
    if (flavor) {
      emitWrappedLines(b, `  备注: ${flavor}`, columns)
    }
  }
  b.rule()

  const dishTotal = calcDishTotal(details)
  b.twoCol('菜品小计:', `${dishTotal}`)
  b.twoCol('配送费:', formatMoney(order.deliveryFee))
  b.bold(true).twoCol('合计:', formatMoney(order.amount)).bold(false)
  b.rule()

  b.size(1, 1)
  b.line(`支付方式: ${getPayMethodText(order.payMethod)}`)
  if (order.checkoutTime) {
    b.line(`支付时间: ${order.checkoutTime}`)
  }

  b.feed(4).cut()

  return b.build()
}

/** 生成测试小票 */
export function buildTestReceipt(options: ReceiptOptions = {}): Uint8Array {
  const columns = options.columns || 32
  const b = new EscPosBuilder(columns)

  b.init().enableChinese().codepageGBK()
  b.align('center').size(2, 2).bold(true).line('打印测试').bold(false).size(1, 1)
  b.newline()
  b.align('left').line('热敏打印机连接成功')
  b.line(`当前时间: ${new Date().toLocaleString('zh-CN')}`)
  b.line('中文测试: 重庆荣昌川菜馆 打印机')
  b.line('数字测试: 0123456789')
  b.line('金额测试: ￥12.34')
  b.feed(4).cut()

  return b.build()
}
